import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Buildings, UsersThree, PaperPlaneTilt, Trash, ArrowSquareOut, Check } from "@phosphor-icons/react";
import OrgAssistantsSection from "@/pages/panels/OrgAssistantsSection";

/**
 * Établissement panel — org overview + seat management + invitations + members.
 *
 * Visible ONLY to admin & org_admin. Behaviour differs by org type:
 *  - Solo cabinet (default) → upsell "Passer à l'abonnement Établissement".
 *  - Establishment → seats used vs paid + members table + invite form.
 */
export default function EstablishmentPanel() {
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState([]);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeSeats, setUpgradeSeats] = useState(2);
  const [upgradeInterval, setUpgradeInterval] = useState("monthly");
  const [orgName, setOrgName] = useState("");
  const [upgradeBusy, setUpgradeBusy] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("admin");
  const [inviteName, setInviteName] = useState("");
  const [inviteCabinet, setInviteCabinet] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [revokeInv, setRevokeInv] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: orgData }, { data: invData }] = await Promise.all([
        api.get("/org/me"),
        api.get("/org/invitations").catch(() => ({ data: [] })),
      ]);
      setOrg(orgData);
      setInvites(invData || []);
      setOrgName(orgData?.organization?.name || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger l'établissement");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const capReached = org && upgradeSeats > (org.self_service_seat_cap || 10);

  const submitUpgrade = async () => {
    if (capReached) return;
    setUpgradeBusy(true);
    try {
      const { data } = await api.post("/org/upgrade", {
        seats: upgradeSeats,
        interval: upgradeInterval,
        origin_url: window.location.origin,
        embedded: false,
        org_name: orgName || org?.organization?.name || null,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Redirection Stripe impossible");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de lancer l'upgrade");
    } finally { setUpgradeBusy(false); }
  };

  const submitInvite = async () => {
    if (!inviteEmail || !/@/.test(inviteEmail)) return toast.error("Email invalide");
    setInviteBusy(true);
    try {
      await api.post("/org/invitations", {
        email: inviteEmail.trim().toLowerCase(),
        role: inviteRole,
        name: inviteName || null,
        cabinet_name: inviteCabinet || null,
      });
      toast.success("Invitation envoyée");
      setInviteOpen(false);
      setInviteEmail(""); setInviteName(""); setInviteCabinet(""); setInviteRole("admin");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    } finally { setInviteBusy(false); }
  };

  const confirmRevoke = async () => {
    if (!revokeInv) return;
    try {
      await api.delete(`/org/invitations/${revokeInv.invitation_id}`);
      toast.success("Invitation révoquée");
      setRevokeInv(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Révocation impossible"); }
  };

  if (loading || !org) {
    return <div className="text-sm text-[#5F758F] py-8" data-testid="establishment-loading">Chargement…</div>;
  }

  const isEstablishment = org.organization?.type === "establishment";
  const seatsUsed = org.seats_used || 0;
  const seatsPaid = org.seats_paid || 1;
  const seatsFree = Math.max(0, seatsPaid - seatsUsed);
  const services = org.services || [];
  const members = org.members || [];

  return (
    <div data-testid="establishment-panel" className="space-y-6">
      {/* Header */}
      <div className="bg-white border border-[#E1E9F0] rounded-2xl p-6 flex items-start gap-5">
        <div className="w-14 h-14 rounded-[14px] bg-[#E6F1F5] border border-[#C7DEE6] flex items-center justify-center shrink-0">
          <Buildings size={26} weight="duotone" className="text-[#3A8FA5]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-[22px] font-bold text-[#1E3A5F] leading-tight">{org.organization?.name || "Mon cabinet"}</h2>
            {isEstablishment ? (
              <span className="text-[11px] tracking-[0.12em] uppercase font-bold px-2.5 py-1 rounded-full bg-[#E6F1F5] text-[#3A8FA5] border border-[#C7DEE6]">Établissement</span>
            ) : (
              <span className="text-[11px] tracking-[0.12em] uppercase font-bold px-2.5 py-1 rounded-full bg-[#EEF3F8] text-[#5F758F] border border-[#E1E9F0]">Solo</span>
            )}
          </div>
          <p className="text-sm text-[#5F758F] mt-1.5">
            {isEstablishment
              ? "Multi-praticiens · facturation par siège · paiement carte ou SEPA"
              : "Cabinet individuel. Passez à l'abonnement Établissement pour inviter d'autres praticiens et partager vos questionnaires au service."}
          </p>
        </div>
        {!isEstablishment && (
          <Button className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold" onClick={() => setUpgradeOpen(true)} data-testid="upgrade-establishment-btn">
            Passer à l'Établissement
          </Button>
        )}
      </div>

      {isEstablishment && (
        <>
          {/* Seats */}
          <div className="bg-white border border-[#E1E9F0] rounded-2xl p-6">
            <p className="text-[11px] tracking-[0.14em] uppercase text-[#5F758F] font-bold mb-3">Sièges praticien</p>
            <div className="flex items-end gap-8 flex-wrap">
              <div>
                <p className="text-[36px] font-bold text-[#1E3A5F] leading-none tabular-nums"><span data-testid="seats-used">{seatsUsed}</span><span className="text-[#5F758F] font-normal"> / {seatsPaid}</span></p>
                <p className="text-[13px] text-[#5F758F] mt-1">{seatsFree > 0 ? `${seatsFree} siège(s) disponible(s)` : "Tous les sièges sont occupés"}</p>
              </div>
              <Button variant="outline" className="rounded-[10px] font-semibold border-[#3A8FA5] text-[#3A8FA5] hover:bg-[#E6F1F5]" onClick={() => setUpgradeOpen(true)} data-testid="add-seats-btn">
                Ajuster le nombre de sièges
              </Button>
            </div>
          </div>

          {/* Services / praticiens */}
          <div className="bg-white border border-[#E1E9F0] rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <UsersThree size={20} weight="duotone" className="text-[#3A8FA5]" />
              <h3 className="text-[16px] font-bold text-[#1E3A5F]">Membres ({members.length})</h3>
              <span className="text-[12px] text-[#5F758F]">— {services.length} praticien{services.length > 1 ? "s" : ""}</span>
              <Button size="sm" className="ml-auto rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold" onClick={() => setInviteOpen(true)} data-testid="invite-btn">
                <PaperPlaneTilt size={14} className="mr-1.5" /> Inviter
              </Button>
            </div>
            <div className="border border-[#E1E9F0] rounded-[10px] overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F7FB] text-[#5F758F]"><tr>
                  <th className="text-left px-4 py-2.5 font-semibold">Nom</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Email</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Service</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Rôle</th>
                </tr></thead>
                <tbody>
                  {members.map((m) => {
                    const roleLabel = {
                      org_admin: "Direction",
                      admin: "Praticien",
                      assistant: "Assistant",
                      research: "Recherche",
                    }[m.role] || m.role;
                    const roleBadge = m.role === "org_admin"
                      ? "bg-[#E6F1F5] text-[#3A8FA5] border-[#C7DEE6]"
                      : "bg-[#EEF3F8] text-[#1E3A5F] border-[#E1E9F0]";
                    return (
                      <tr key={m.user_id} className="border-t border-[#E1E9F0]" data-testid={`member-${m.user_id}`}>
                        <td className="px-4 py-2.5 text-[#1E3A5F] font-medium">{m.name || "—"}</td>
                        <td className="px-4 py-2.5 text-[#5F758F]">{m.email}</td>
                        <td className="px-4 py-2.5 text-[#5F758F]">{m.cabinet_name || (m.role === "admin" || m.role === "org_admin" ? "—" : "(rattaché)")}</td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border uppercase tracking-[0.08em] ${roleBadge}`}>
                            {roleLabel}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pending invitations */}
          {invites.length > 0 && (
            <div className="bg-white border border-[#E1E9F0] rounded-2xl p-6">
              <h3 className="text-[16px] font-bold text-[#1E3A5F] mb-4">Invitations en attente ({invites.length})</h3>
              <div className="space-y-2">
                {invites.map((inv) => (
                  <div key={inv.invitation_id} className="flex items-center gap-3 border border-[#E1E9F0] rounded-[10px] px-4 py-2.5" data-testid={`invite-${inv.invitation_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#1E3A5F] truncate">{inv.email}</p>
                      <p className="text-[12px] text-[#5F758F]">
                        {inv.role === "admin" ? "Praticien" : inv.role === "assistant" ? "Assistant" : "Recherche"}
                        {inv.cabinet_name ? ` · ${inv.cabinet_name}` : ""}
                        {" · expire le "}{new Date(inv.expires_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" className="rounded-[10px] text-[#5F758F] hover:text-[#D45A3F] hover:bg-[#FFE0DC]" onClick={() => setRevokeInv(inv)} data-testid={`revoke-${inv.invitation_id}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Assistants multi-praticiens */}
          <OrgAssistantsSection isEstablishment={isEstablishment} />
        </>
      )}

      {/* Upgrade dialog */}
      <Dialog open={upgradeOpen} onOpenChange={(v) => !upgradeBusy && setUpgradeOpen(v)}>
        <DialogContent className="bg-white sm:max-w-lg">
          <DialogHeader>
            <div className="w-12 h-12 rounded-[14px] bg-[#E6F1F5] border border-[#C7DEE6] flex items-center justify-center mb-2">
              <Buildings size={22} weight="duotone" className="text-[#3A8FA5]" />
            </div>
            <DialogTitle className="text-2xl font-bold tracking-tight text-[#1E3A5F]">{isEstablishment ? "Ajuster les sièges" : "Passer à l'abonnement Établissement"}</DialogTitle>
            <DialogDescription className="text-[#5F758F] leading-relaxed">
              {isEstablishment
                ? "Modifier le nombre de sièges praticien. Les changements sont facturés au prorata."
                : "Un siège = un praticien. Les assistants et assistants de recherche sont inclus sans surcoût."}
              {" "}Paiement par carte bancaire ou <strong>prélèvement SEPA</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!isEstablishment && (
              <div>
                <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Nom de l'établissement</Label>
                <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="Clinique du Marais…" className="mt-2 rounded-[10px]" data-testid="upgrade-org-name" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Nombre de sièges</Label>
                <Input type="number" min="2" max="20" value={upgradeSeats} onChange={(e) => setUpgradeSeats(Math.max(2, parseInt(e.target.value) || 2))} className="mt-2 rounded-[10px]" data-testid="upgrade-seats" />
              </div>
              <div>
                <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Facturation</Label>
                <Select value={upgradeInterval} onValueChange={setUpgradeInterval}>
                  <SelectTrigger className="mt-2 rounded-[10px]" data-testid="upgrade-interval"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Mensuel · 39€ HT/siège/mois</SelectItem>
                    <SelectItem value="yearly">Annuel · 35,10€ HT/siège/mois (10% économisés)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {capReached ? (
              <div className="bg-[#FFF3D6] border border-[#F0DDA8] rounded-[10px] px-4 py-3 text-[13px] text-[#1E3A5F]">
                <strong>Au-delà de 10 sièges</strong>, contactez-nous pour un devis personnalisé adapté aux hôpitaux et grands groupes.
                <br/><a href="mailto:contact@questiomed.com?subject=Devis Établissement (11+ sièges)" className="inline-flex items-center gap-1 text-[#3A8FA5] font-semibold underline underline-offset-2 mt-1">contact@questiomed.com <ArrowSquareOut size={12} /></a>
              </div>
            ) : (
              <div className="bg-[#F4F7FB] border border-[#E1E9F0] rounded-[10px] px-4 py-3 text-[13px] text-[#5F758F]">
                Total estimé : <strong className="text-[#1E3A5F]">
                  {upgradeInterval === "yearly"
                    ? `${(upgradeSeats * 35.10).toFixed(2)}€ HT/mois`
                    : `${(upgradeSeats * 39).toFixed(0)}€ HT/mois`}
                </strong> · {upgradeSeats} praticien{upgradeSeats > 1 ? "s" : ""}, assistants illimités inclus.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-[10px] font-semibold" onClick={() => setUpgradeOpen(false)} disabled={upgradeBusy}>Annuler</Button>
            <Button className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold" onClick={submitUpgrade} disabled={upgradeBusy || capReached} data-testid="upgrade-submit">
              {upgradeBusy ? "Redirection…" : "Continuer vers Stripe"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={(v) => !inviteBusy && setInviteOpen(v)}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <div className="w-12 h-12 rounded-[14px] bg-[#E6F1F5] border border-[#C7DEE6] flex items-center justify-center mb-2">
              <PaperPlaneTilt size={22} weight="duotone" className="text-[#3A8FA5]" />
            </div>
            <DialogTitle className="text-2xl font-bold tracking-tight text-[#1E3A5F]">Inviter un membre</DialogTitle>
            <DialogDescription className="text-[#5F758F] leading-relaxed">Un email d'invitation avec un lien valable 7 jours sera envoyé.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Email</Label>
              <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="prenom.nom@example.com" className="mt-2 rounded-[10px]" data-testid="invite-email" />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Rôle</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="mt-2 rounded-[10px]" data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Praticien (consomme 1 siège)</SelectItem>
                  <SelectItem value="assistant">Assistant (inclus)</SelectItem>
                  <SelectItem value="research">Assistant de recherche (inclus)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {inviteRole === "admin" && (
              <div>
                <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Nom du service (optionnel)</Label>
                <Input value={inviteCabinet} onChange={(e) => setInviteCabinet(e.target.value)} placeholder="Service de Cardiologie…" className="mt-2 rounded-[10px]" data-testid="invite-cabinet" />
              </div>
            )}
            <div>
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Nom (optionnel)</Label>
              <Input value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="Dr Martin Dupont" className="mt-2 rounded-[10px]" data-testid="invite-name" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-[10px] font-semibold" onClick={() => setInviteOpen(false)} disabled={inviteBusy}>Annuler</Button>
            <Button className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold" onClick={submitInvite} disabled={inviteBusy} data-testid="invite-submit">
              {inviteBusy ? "Envoi…" : "Envoyer l'invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke confirmation */}
      <AlertDialog open={!!revokeInv} onOpenChange={(v) => !v && setRevokeInv(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-bold text-[#1E3A5F]">Révoquer cette invitation ?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5F758F]">Le lien envoyé à <strong className="text-[#1E3A5F]">{revokeInv?.email}</strong> deviendra invalide. Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-[10px]">Retour</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRevoke} className="rounded-[10px] bg-[#8B2A1E] hover:bg-[#6D2117] text-white font-semibold" data-testid="revoke-confirm">Révoquer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
